// noinspection HtmlRequiredAltAttribute

import {createGrid} from "../../../helpers/grids/grids";
import createElement from "../../../helpers/createElement";
import {createCellRenderer} from "../../../helpers/grids/helpers";
import {getSingleInputElementByNameV3} from "../../../helpers/forms/formInputsHelpers";
import {PickAuditionRequestModal} from "../audition-requests/PickAuditionRequestModal/PickAuditionRequestModal";
import Axios from "axios";
import handleAjaxError from "../../../helpers/handleAjaxError";
import {loading, stopLoading} from "../../../helpers/loading";
import {handleAjaxSuccess, swalConfirm} from "../../../helpers/swalHelper";
import Swal from "sweetalert2";
import {waitUntil} from "../../../helpers/waitUntil";
import {randomUniqueClassName} from "../../../helpers/inputs/random/strings";
import {initTomSelectInput} from "../../../helpers/inputs/tom-select/tom-select";
import {displayLocalValidationError} from "../../../helpers/forms/validationErrors/utils";

function initProductionRolesIndex() {
	const castStatusSelect = getSingleInputElementByNameV3(document.body, 'cast_status_select');

	const grid = createGrid('.roles-grid', {
			columnDefs: [
				{
					field: "role",
					headerName: 'Role',
					sortable: true,
					suppressAutoSize: true,
					// autoHeight: true, Broken, causes some weird issues sometimes where the big rows
					// move real slow before settling into their actual height
					minWidth: 380,
					maxWidth: 400,
					cellRenderer: createCellRenderer(params => {
						// noinspection JSUnresolvedVariable
						const cell = createElement(`
                            <div class="role" style="line-height: 1.2">
                                <div class="container cast-container">
                                	<div class="row">
                                		<div class="col">
                                			<div class="d-flex align-items-center">
                                				<h5 class="my-0">${params.data.role}</h5>
                                				<button class="btn btn-primary text-nowrap btn-sm ml-auto assign-actor-button ${params.data.can_edit ? '' : 'd-none'} my-2">
                                					Cast Actor
												</button>
											</div>
										</div>
									</div>
									${getCastContainerHtml(params.data)}
								</div>
                            </div>`
						)

						const addActorButton = cell.querySelector('.assign-actor-button');

						if (addActorButton) {
							addActorButton.addEventListener('click', async () => {
								// noinspection JSUnresolvedReference
								await handleAddActorRequest(
									params.data.pick_actor_modal_route,
									params.data.assign_actor_route,
									params.data.role,
								);
							})
						}

						// Setup delete buttons
						cell.querySelectorAll('.remove-actor-button').forEach(button => {
							button.addEventListener('click', () => {
								// noinspection JSUnresolvedReference
								handleDeleteActorRequest(
									button.dataset.route,
									button.dataset.actorName,
									params.data.role,
								)
							})
						})

						return cell;
					})
				},
				{
					maxWidth: 160,
					field: "role_gender",
					headerName: "Role Gender",
					sortable: true,
				},
				{
					field: "auditions_count",
					headerName: "Auditions",
					sortable: true,
				},
				{
					maxWidth: 90,
					field: 'isCast',
					headerName: 'Cast',
					sortable: true,
					cellRenderer: createCellRenderer(params => {
						// noinspection JSUnresolvedVariable
						return params.data.isCast ? 'Yes' : 'No';
					})
				},
				{
					field: "public_id",
					headerName: '',
					maxWidth: 240,
					cellRenderer: createCellRenderer(params => {
						// noinspection JSUnresolvedVariable
						const buttons = createElement(`
                            <div class="buttons">
                                <a class="btn btn-primary btn-sm ${!params.data.can_edit ? 'd-none' : ''}" href="${params.data.edit_url}" target="_blank">
                                    <i class="fa-solid fa-user-pen"></i>
                                    Edit
								</a>
                                <button type="button" class="btn btn-danger btn-sm ${!params.data.can_delete ? 'd-none' : ''} remove-role-button">
                                    <i class="fa-solid fa-trash"></i>
                                    Remove
                                </button>
                            </div>`
						)

						const deleteButton = buttons.querySelector('.remove-role-button');

						deleteButton.onclick =
							() => removeRole(params.data);

						return buttons;
					})
				},
			],
			animateRows: false,
			paginationPageSize: 200,
			cacheBlockSize: 400,
			getRowHeight: row => {
				try {
					const role = row.data;
					// noinspection JSUnresolvedReference
					const castDataArray = role.castDataArray;
					const leadCastArray = castDataArray.filter(data => {
						return data.type === 'primary';
					})

					const understudyCastArray = castDataArray.filter(data => {
						return data.type === 'understudy';
					})

					let highestCastArray = leadCastArray.length > understudyCastArray.length ?
						leadCastArray :
						understudyCastArray

					const padding = 100;

					const imageHeight = 80
					let imagesRowHeight = imageHeight; // Default if no images

					// If we have images...
					if (highestCastArray.length) {
						imagesRowHeight = 0;

						// Separate into rows of 2
						for (let x = 0; x < highestCastArray.length; x += 2) {
							const row = highestCastArray.slice(x, x + 2);
							// For each row we add 1 row height
							imagesRowHeight += imageHeight;

							// And for each row, find the longest length name
							const longestNameLength = row.reduce((carry, entry) => {
								if (carry < entry.name.length) {
									return entry.name.length;
								} else {
									return carry;
								}
							}, 0)

							// For each 7 chars add some padding
							const groupsOf10Chars = Math.ceil(longestNameLength / 7)
							imagesRowHeight += groupsOf10Chars * 14; // 14px padding per extra 10 chars
						}
					}

					return padding + imagesRowHeight;

				} catch (e) {
					console.error('Error pre calculating row height')
					console.error(row)
					console.error(e)

					return 160;
				}
			},
			_rowName: 'Roles',
			rowClass: 'cursor-pointer',
		},
		window['routes/admin.productions.roles.grid'],
		() => {
			return {
				castStatus: castStatusSelect.value,
			}
		}
	);

	castStatusSelect.addEventListener('change', () => {
		grid._refresh();
	})

	const copyRolesButton = document.querySelector('.copy-roles-button');
	if (copyRolesButton) {
		try {
			/** @var {Array} */
			const roleOptions = JSON.parse(copyRolesButton.dataset.sources);

			if (roleOptions.length) {
				copyRolesButton.addEventListener('click', () => {
					displayCopyRolesPopup(roleOptions, copyRolesButton.dataset.route);
				})
			}
		} catch (e) {
			console.log('Could not initialize copy roles button');
			console.log(e);
		}
	}

	/** Methods */

	function getCastContainerHtml(role) {

		// noinspection JSUnresolvedReference
		/** @var {Array<Object<string,any>>} */
		let castDataArray = role.castDataArray;

		// Sort by name first
		castDataArray = castDataArray.sort((a, b) => {
			return a.name.localeCompare(b.name)
		})

		const leadCastArray = castDataArray.filter(data => {
			return data.type === 'primary';
		})

		const understudyCastArray = castDataArray.filter(data => {
			return data.type === 'understudy';
		})

		let leadCastHtml;
		let understudyCastHtml;

		if (leadCastArray.length) {
			leadCastHtml = leadCastArray.map(getActorHtml).join('')
		} else {
			leadCastHtml = '<div class="col my-2"><div class="text-muted text-center mono-font">NO CASTS</div></div>'
		}

		if (understudyCastArray.length) {
			understudyCastHtml = understudyCastArray.map(getActorHtml).join('')
		} else {
			understudyCastHtml = '<div class="col my-2"><div class="text-muted text-center mono-font">NO CASTS</div></div>'
		}

		return `
			<div class="row pb-2">
				<div class="col-6">
					<div class="row border-bottom">
						<div class="col">
							<div class="">LEAD CAST</div>
						</div>
					</div>
					<div class="row flex-wrap pt-2">
						${leadCastHtml}
					</div>
				</div>
				<div class="col-6 border-left">
					<div class="row border-bottom">
						<div class="col">
							<div class="">UNDERSTUDY</div>
						</div>
					</div>
					<div class="row flex-wrap pt-2">
						${understudyCastHtml}
					</div>
				</div>
			</div>
		`;
	}

	function getActorHtml(castData) {
		// noinspection JSUnresolvedReference
		return `
			<div class="cast-entry">
				<div class="photo-wrapper border">
					${castData.photo ? `<img src="${castData.photo}" loading="lazy">` : `<div class="text-muted text-center no-photo-message">No<br>Photo</div>`}
				</div>
				<div class="text-center">${castData.name}</div>
				<button
					class="btn btn-danger btn-sm remove-actor-button"
					title="Remove Actor"
					data-actor-name="${castData.name}"
					data-route="${castData.remove_route}"
				>
					<i class="fa-solid fa-xmark"></i>
				</button>
			</div>
		`;
	}

	function removeRole(role) {
		swalConfirm({
			title: 'Are you sure you want to delete this role?'
		}).then(response => {
			if (response.isConfirmed) {
				loading();

				// noinspection JSUnresolvedVariable
				Axios.post(role.remove_url)
					.then(handleAjaxSuccess)
					.catch(handleAjaxError)
					.finally(stopLoading);
			}
		})
	}

	async function handleAddActorRequest(modalRoute, assignRoute, roleName) {
		const modal = await new PickAuditionRequestModal(
			modalRoute,
			{
				onActorSelect(actor) {
					// Hide modal
					modal.hide();

					// Prompt for actor types
					Swal.fire({
						title: `Add ${actor.full_name} to role '${roleName}' as a`,
						input: "select",

						inputOptions: {
							'primary': 'Lead',
							'understudy': 'Understudy',
						},

						showConfirmButton: true,
						showCancelButton: true,
						cancelButtonText: 'Cancel',
					}).then(result => {
						if (result.isConfirmed) {
							// Submit request to server
							loading();

							// Do stuff
							Axios.post(assignRoute, {
								actorId: actor.public_id,
								type: result.value,
							})
								.then(response => {
									// Refresh grid
									grid._silentRefresh();

									// Show success message but don't change view
									handleAjaxSuccess(response, () => {
										// Don't reload page
									})
								})
								.catch(handleAjaxError)
								.finally(stopLoading);
						} else {
							// Bring modal back
							modal.display(false);
						}
					})


				}
			}
		)
	}

	function handleDeleteActorRequest(removeRoute, actorName, roleName) {
		Swal.fire({
			title: `Remove ${actorName} from role '${roleName}'`,
			showConfirmButton: true,
			showCancelButton: true,
		}).then(result => {
			if (result.isConfirmed) {
				// Submit request to server
				loading();

				Axios.post(removeRoute)
					.then(response => {
						// Refresh grid
						grid._silentRefresh();

						// Show success message but don't change view
						handleAjaxSuccess(response, () => {
							// Don't reload page
						})
					})
					.catch(handleAjaxError)
					.finally(stopLoading);
			}
		})
	}

	/**
	 * @param {Array} options
	 * @param {string} postRoute
	 */
	function displayCopyRolesPopup(options, postRoute) {
		const swalInputClass = randomUniqueClassName('copy_roles_swal_input');

		Swal.fire({
			title: 'Copy Roles From Production',
			showCancelButton: true,
			html: `<input type="text" autocomplete="off" class="form-control ${swalInputClass}">`,
		}).then(result => {
			if (result.isConfirmed) {
				const input = document.querySelector(`.${swalInputClass}`);
				if (input.value === '') {
					displayLocalValidationError(document.body, 'dummy', 'You must select a production to copy from');
					return;
				}

				loading();

				Axios.post(
					postRoute,
					{
						from_production_id: input.value,
					}
				)
					.then(response => {
						stopLoading();

						// Show success message but don't change view
						Swal.fire({
							icon: 'success',
							html: response.data.html,
							timer: 3500,
						}).then();

						// Refresh grid
						grid._silentRefresh();
					})
					.catch(handleAjaxError)
					.finally(stopLoading);
			}
		})

		// Initialize production select
		waitUntil(() => document.querySelector(`.${swalInputClass}`))
			.then(() => {
				initTomSelectInput(
					document.querySelector(`.${swalInputClass}`),
					{
						options: options,
						maxItems: 1,

						// This ensures the dropdown is appended to body
						dropdownParent: 'body',
						render: {
							dropdown: function () {
								// Set a higher z-index than SweetAlert's modal (which is typically 1060)
								return '<div class="ts-dropdown" style="z-index: 1061"></div>';
							}
						}
					}
				)
			})
	}
}

export {
	initProductionRolesIndex
}
